import React, { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable } from "react-native";
import { View, Text, Button } from "@src/components";
import { Icon } from "@src/components/Icon";
import { UniversalDatePicker } from "@src/components/UniversalDatePicker";
import { MultiSelect, SearchInputField, Autocomplete, Checkbox } from "@visioni/a3s-ui/components";
import { useClickOutside } from "@visioni/a3s-ui/hooks";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { sizes } from "@src/constants/sizes";
import {
  VEHICLE_TYPE_LABELS,
  VEHICLE_TYPE_VALUES,
  VEHICLE_TYPE_GROUP,
  LOAD_STATUSES,
  TRAY_TYPES,
  getAttributeLabel,
  getTrayTypeLabel,
} from "@visioni/a3s-vehicle-tracker";
import type { VehicleFilters, TrayType } from "@visioni/a3s-vehicle-tracker";
import { useAttributeFilters } from "../hooks/use-attribute-filters";
import {
  useCameras,
  daysAgo,
  extractTimeInZone,
  combineDateAndTimeInZone,
  formatDateTime,
  getTimezoneAbbreviation,
} from "@visioni/a3s-vehicle-tracker/app";
import { useCameras as useAllCameras } from "@src/features/cameras";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { AssetId } from "@visioni/a3s-schema";
import { StyleSheet, useUnistyles, UnistylesRuntime } from "react-native-unistyles";

const noop = () => {};

interface FilterPanelProps {
  filters: VehicleFilters;
  onFiltersChange: <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) => void;
  onClearAll: () => void;
  selectedSiteId: string | null;
  onSiteChange: (siteId: string | null) => void;
  sortOrder: "asc" | "desc";
  onSortChange: (order: "asc" | "desc") => void;
  activeTimezone: string;
  timezoneOptions?: string[];
  needsTimezoneSelector?: boolean;
  onTimezoneChange?: (tz: string) => void;
  /** Opens the export side panel. The button never starts a download itself. */
  onExportPress?: () => void;
}

interface DatePreset {
  label: string;
  days: number;
}

interface CameraOption {
  assetId: string;
  cameraId: string;
}

const DATE_PRESETS: DatePreset[] = [
  { label: "Last 24h", days: 1 },
  { label: "Last 7d", days: 7 },
  { label: "Last 30d", days: 30 },
  { label: "Last 90d", days: 90 },
];

function getActivePresetLabel(filters: VehicleFilters): string {
  for (const preset of DATE_PRESETS) {
    const expectedStart = new Date();
    expectedStart.setDate(expectedStart.getDate() - preset.days);
    const startDiff = Math.abs(new Date(filters.startDate).getTime() - expectedStart.getTime());
    const endDiff = Math.abs(new Date(filters.endDate).getTime() - Date.now());
    if (startDiff < 60_000 && endDiff < 60_000) return preset.label;
  }
  return "Custom";
}

function countActiveFilters(filters: VehicleFilters): number {
  let count = 0;
  if (filters.plateText.trim()) count++;
  if (filters.make.trim()) count++;
  if (filters.model.trim()) count++;
  if (filters.color.trim()) count++;
  if (filters.description.trim()) count++;
  if (filters.types.length > 0) count++;
  if (Object.keys(filters.selectedCameras).some((k) => (filters.selectedCameras[k] ?? []).length > 0)) count++;
  if (filters.loadStatuses.length > 0) count++;
  if (Object.keys(filters.attributes).length > 0) count++;
  return count;
}

function dateRangeLabel(filters: VehicleFilters, timeZone: string): string {
  const label = getActivePresetLabel(filters);
  if (label !== "Custom") return label;
  const start = formatDateTime(filters.startDate, timeZone);
  const end = formatDateTime(filters.endDate, timeZone);
  return `${start} – ${end}`;
}

function DateRangePopover({
  filters,
  onFiltersChange,
  onClose,
  activeTimezone,
}: {
  filters: VehicleFilters;
  onFiltersChange: <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) => void;
  onClose: () => void;
  activeTimezone: string;
}) {
  const { theme } = useUnistyles();
  const [customStart, setCustomStart] = useState(filters.startDate);
  const [customEnd, setCustomEnd] = useState(filters.endDate);
  const [startTime, setStartTime] = useState(extractTimeInZone(filters.startDate, activeTimezone));
  const [endTime, setEndTime] = useState(extractTimeInZone(filters.endDate, activeTimezone));
  const activePreset = getActivePresetLabel(filters);

  useEffect(() => {
    setStartTime(extractTimeInZone(customStart, activeTimezone));
    setEndTime(extractTimeInZone(customEnd, activeTimezone));
  }, [activeTimezone, customStart, customEnd]);

  const applyPreset = (days: number) => {
    onFiltersChange("startDate", daysAgo(days));
    onFiltersChange("endDate", new Date().toISOString());
    onClose();
  };

  const applyCustom = () => {
    onFiltersChange("startDate", combineDateAndTimeInZone(customStart, startTime, activeTimezone));
    onFiltersChange("endDate", combineDateAndTimeInZone(customEnd, endTime, activeTimezone));
    onClose();
  };

  const endBeforeStart =
    new Date(combineDateAndTimeInZone(customEnd, endTime, activeTimezone)).getTime() <
    new Date(combineDateAndTimeInZone(customStart, startTime, activeTimezone)).getTime();

  return (
    <View
      style={{
        position: "absolute",
        top: "100%",
        right: 0,
        marginTop: 6,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.foreground,
        padding: 16,
        minWidth: 300,
        zIndex: 2000,
        ...({ boxShadow: "0 8px 24px rgba(0,0,0,0.15)" } as Record<string, string>),
      }}
    >
      {/* Presets */}
      <View gap="xs" mb="md">
        <Text textStyle="xs/semibold" color="textMuted" mb="xs">
          Quick presets
        </Text>
        {DATE_PRESETS.map((preset) => (
          <Pressable
            key={preset.days}
            onPress={() => applyPreset(preset.days)}
            style={{
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 6,
              backgroundColor: activePreset === preset.label ? theme.colors.muted : "transparent",
            }}
          >
            <Text textStyle="sm/regular" color={activePreset === preset.label ? "text" : "textMuted"}>
              {preset.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: theme.colors.border }} mb="md" />

      {/* Custom range */}
      <View gap="sm">
        <Text textStyle="xs/semibold" color="textMuted">
          Custom range
        </Text>
        <View flexDirection="row" gap="md" alignItems="flex-end" flexWrap="wrap">
          <View style={{ minWidth: 120 }}>
            <Text textStyle="xxs/regular" color="textMuted" mb="xs">
              Start date
            </Text>
            <UniversalDatePicker value={customStart} onChange={setCustomStart} size="small" />
          </View>
          <View style={{ minWidth: 90 }}>
            <Text textStyle="xxs/regular" color="textMuted" mb="xs">
              Start time
            </Text>
            {createElement("input", {
              type: "time",
              value: startTime,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => setStartTime(e.target.value),
              style: {
                height: 32,
                borderRadius: 6,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                padding: "0 8px",
                fontSize: 13,
              },
            })}
          </View>
        </View>
        <View flexDirection="row" gap="md" alignItems="flex-end" flexWrap="wrap">
          <View style={{ minWidth: 120 }}>
            <Text textStyle="xxs/regular" color="textMuted" mb="xs">
              End date
            </Text>
            <UniversalDatePicker value={customEnd} onChange={setCustomEnd} size="small" />
          </View>
          <View style={{ minWidth: 90 }}>
            <Text textStyle="xxs/regular" color="textMuted" mb="xs">
              End time
            </Text>
            {createElement("input", {
              type: "time",
              value: endTime,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEndTime(e.target.value),
              style: {
                height: 32,
                borderRadius: 6,
                border: `1px solid ${theme.colors.border}`,
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                padding: "0 8px",
                fontSize: 13,
              },
            })}
          </View>
        </View>
        <View flexDirection="row" gap="sm" mt="sm" justifyContent="flex-end" alignItems="center">
          {endBeforeStart && (
            <Text textStyle="xs/regular" color="danger">
              End must be after start
            </Text>
          )}
          <Button variant="secondary" size="sm" onPress={onClose} label="Cancel" />
          <Button variant="primary" size="sm" onPress={applyCustom} disabled={endBeforeStart} label="Apply" />
        </View>
      </View>
    </View>
  );
}

export function FilterPanel({
  filters,
  onFiltersChange,
  onClearAll,
  selectedSiteId,
  onSiteChange,
  sortOrder,
  onSortChange,
  activeTimezone,
  timezoneOptions,
  needsTimezoneSelector,
  onTimezoneChange,
  onExportPress,
}: FilterPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [tzDropdownOpen, setTzDropdownOpen] = useState(false);
  const { theme } = useUnistyles();
  const breakpoint = (UnistylesRuntime.breakpoint as string) ?? "md";
  const isNarrow = breakpoint === "xs" || breakpoint === "sm";
  const { data: camerasData } = useCameras({ siteId: selectedSiteId });
  const { data: allCamerasData } = useAllCameras();
  const dateChipRef = useRef(null);
  const sortChipRef = useRef(null);
  const tzChipRef = useRef(null);
  const panelRef = useRef(null);

  useClickOutside(
    panelRef,
    useCallback(() => setExpanded(false), []),
    expanded,
  );

  useEffect(() => {
    if (!datePopoverOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = dateChipRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setDatePopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [datePopoverOpen]);

  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = sortChipRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [sortDropdownOpen]);

  useEffect(() => {
    if (!tzDropdownOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const node = tzChipRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setTzDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [tzDropdownOpen]);

  const cameraGroups = useMemo(() => {
    if (!camerasData?.cameras) return [];
    return camerasData.cameras;
  }, [camerasData]);

  const siteOptions = useMemo(() => {
    if (!allCamerasData) return [];
    return groupCamerasBySite(allCamerasData, "site").map((g) => ({
      siteId: g.siteId,
      siteName: g.siteName,
    }));
  }, [allCamerasData]);

  const selectedSiteOption = useMemo(
    () => siteOptions.find((s) => s.siteId === selectedSiteId) ?? null,
    [siteOptions, selectedSiteId],
  );

  const allCameraOptions: CameraOption[] = useMemo(
    () =>
      cameraGroups
        .flatMap((g) => g.cameras.map((c) => ({ assetId: g.assetId, cameraId: c })))
        .sort((a, b) => a.assetId.localeCompare(b.assetId) || a.cameraId.localeCompare(b.cameraId)),
    [cameraGroups],
  );

  const selectedCameraOptions: CameraOption[] = useMemo(
    () =>
      Object.entries(filters.selectedCameras).flatMap(([assetId, cameras]) =>
        (cameras ?? []).map((cameraId) => ({ assetId, cameraId })),
      ),
    [filters.selectedCameras],
  );

  const { isTowingTrailer, trayValue, signTextValue, handleTrailerToggle, handleTrayChange, handleSignTextChange } =
    useAttributeFilters(filters.attributes, onFiltersChange);

  const activeCount = useMemo(() => {
    let count = countActiveFilters(filters);
    if (selectedSiteId) count++;
    return count;
  }, [filters, selectedSiteId]);
  const dateLabel = useMemo(() => dateRangeLabel(filters, activeTimezone), [filters, activeTimezone]);

  const handleCamerasChange = useCallback(
    (value: readonly CameraOption[]) => {
      const record: Record<string, string[]> = {};
      for (const opt of value) {
        const arr = record[opt.assetId] ?? [];
        arr.push(opt.cameraId);
        record[opt.assetId] = arr;
      }
      onFiltersChange("selectedCameras", record);
    },
    [onFiltersChange],
  );

  const loadStatusOptions = useMemo(() => [...LOAD_STATUSES] as string[], []);

  return (
    <View ref={panelRef} mb="lg" gap="md" px="lg" style={{ zIndex: 1000 }}>
      {/* Compact bar: primary search + date + filters + sort + clear */}
      <View flexDirection="row" alignItems="center" gap="md" flexWrap="wrap" style={{ zIndex: 2 }}>
        <View style={isNarrow ? { flex: 1, minWidth: "100%" } : { flex: 1, minWidth: 200, maxWidth: 320 }}>
          <SearchInputField
            value={filters.plateText}
            onChangeText={(v) => onFiltersChange("plateText", v)}
            onFocus={noop}
            onKeyPress={noop}
            placeholder="Search plate..."
            size="small"
            validate={(v) => {
              const trimmed = v.trim();
              if (trimmed.length > 0 && trimmed.length < 4) return "Min. 4 characters";
              return undefined;
            }}
          />
        </View>

        {/* Date range chip + popover */}
        <View
          ref={dateChipRef}
          style={{ position: "relative", zIndex: 1000, ...(isNarrow ? { flex: 1, minWidth: "100%" } : {}) }}
        >
          <Pressable
            onPress={() => setDatePopoverOpen((v) => !v)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: theme.colors.foreground,
              borderWidth: 1,
              borderColor: datePopoverOpen ? theme.colors.interactive.buttons.default_background : theme.colors.border,
            }}
          >
            <Icon name="calendar" size={14} color={theme.colors.text} />
            <Text textStyle="sm/medium" color="text">
              {dateLabel}
            </Text>
            <Icon name={datePopoverOpen ? "chevronUp" : "chevronDown"} size={10} color={theme.colors.textMuted} />
          </Pressable>
          {datePopoverOpen && (
            <DateRangePopover
              filters={filters}
              onFiltersChange={onFiltersChange}
              onClose={() => setDatePopoverOpen(false)}
              activeTimezone={activeTimezone}
            />
          )}
        </View>

        {/* Timezone chip (only when multiple site timezones exist) */}
        {needsTimezoneSelector && timezoneOptions && onTimezoneChange && (
          <View ref={tzChipRef} style={{ position: "relative", ...(isNarrow ? { flex: 1, minWidth: "100%" } : {}) }}>
            <Pressable
              onPress={() => setTzDropdownOpen((v) => !v)}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: tzDropdownOpen ? theme.colors.interactive.buttons.default_background : theme.colors.border,
                backgroundColor: theme.colors.foreground,
              }}
            >
              <Icon name="clock" size={14} color={theme.colors.text} />
              <Text textStyle="sm/medium" color="text">
                {getTimezoneAbbreviation(activeTimezone)}
              </Text>
              <Icon name={tzDropdownOpen ? "chevronUp" : "chevronDown"} size={10} color={theme.colors.textMuted} />
            </Pressable>
            {tzDropdownOpen && (
              <View
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: 6,
                  minWidth: 220,
                  backgroundColor: theme.colors.foreground,
                  borderRadius: sizes.radius,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  zIndex: 1000,
                  overflow: "hidden",
                  ...({ boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } as Record<string, string>),
                }}
              >
                {timezoneOptions.map((tz) => (
                  <Pressable
                    key={tz}
                    onPress={() => {
                      onTimezoneChange(tz);
                      setTzDropdownOpen(false);
                    }}
                    style={({ hovered }: { hovered?: boolean }) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingVertical: 10,
                      paddingHorizontal: 14,
                      backgroundColor: hovered ? theme.colors.muted : "transparent",
                    })}
                  >
                    <Text textStyle={activeTimezone === tz ? "sm/semibold" : "sm/regular"} color="text">
                      {getTimezoneAbbreviation(tz)} ({tz.replace(/_/g, " ")})
                    </Text>
                    <Icon
                      name="checkmark"
                      size={16}
                      color={activeTimezone === tz ? theme.colors.blue[500] : "transparent"}
                    />
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Filters chip */}
        <Pressable
          onPress={() => setExpanded((v) => !v)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: activeCount > 0 ? theme.colors.interactive.buttons.default_background : theme.colors.border,
            backgroundColor: theme.colors.foreground,
            ...(isNarrow ? { flex: 1, minWidth: "100%" } : {}),
          }}
        >
          <Icon name="tune" size={16} color={theme.colors.text} />
          <Text textStyle="sm/medium" color="text">
            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </Text>
          <Icon name={expanded ? "chevronUp" : "chevronDown"} size={12} color={theme.colors.textMuted} />
        </Pressable>

        {/* Sort dropdown */}
        <View ref={sortChipRef} style={{ position: "relative", ...(isNarrow ? { flex: 1, minWidth: "100%" } : {}) }}>
          <Pressable
            onPress={() => setSortDropdownOpen((v) => !v)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: sizes.radius,
              borderWidth: 1,
              borderColor: sortDropdownOpen ? theme.colors.blue[500] : theme.colors.border,
              backgroundColor: theme.colors.foreground,
            }}
          >
            <Icon name="sort" size={14} color={theme.colors.text} />
            <Text textStyle="sm/medium" color="text">
              Sort
            </Text>
            <Icon name={sortDropdownOpen ? "chevronUp" : "chevronDown"} size={10} color={theme.colors.textMuted} />
          </Pressable>
          {sortDropdownOpen && (
            <View
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 6,
                minWidth: 160,
                backgroundColor: theme.colors.foreground,
                borderRadius: sizes.radius,
                borderWidth: 1,
                borderColor: theme.colors.border,
                zIndex: 1000,
                overflow: "hidden",
                ...({ boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } as Record<string, string>),
              }}
            >
              <Pressable
                onPress={() => {
                  onSortChange("desc");
                  setSortDropdownOpen(false);
                }}
                style={({ hovered }: { hovered?: boolean }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  backgroundColor: hovered ? theme.colors.muted : "transparent",
                })}
              >
                <Text textStyle={sortOrder === "desc" ? "sm/semibold" : "sm/regular"} color="text">
                  Newest first
                </Text>
                <Icon
                  name="checkmark"
                  size={16}
                  color={sortOrder === "desc" ? theme.colors.blue[500] : "transparent"}
                />
              </Pressable>
              <Pressable
                onPress={() => {
                  onSortChange("asc");
                  setSortDropdownOpen(false);
                }}
                style={({ hovered }: { hovered?: boolean }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  backgroundColor: hovered ? theme.colors.muted : "transparent",
                })}
              >
                <Text textStyle={sortOrder === "asc" ? "sm/semibold" : "sm/regular"} color="text">
                  Oldest first
                </Text>
                <Icon name="checkmark" size={16} color={sortOrder === "asc" ? theme.colors.blue[500] : "transparent"} />
              </Pressable>
            </View>
          )}
        </View>

        {/* Clear filters (borderless, only when active) */}
        {activeCount > 0 && (
          <Pressable
            onPress={onClearAll}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              paddingHorizontal: 8,
              paddingVertical: 8,
            }}
          >
            <Text textStyle="sm/medium" color="textMuted">
              Clear filters
            </Text>
          </Pressable>
        )}

        {/* Export: right-aligned on wide layouts, full width when the bar wraps. Only opens the export panel. */}
        {onExportPress && (
          <View style={isNarrow ? exportStyles.narrow : exportStyles.wide}>
            <Button
              variant="primary"
              size="sm"
              label="Export"
              accessibilityLabel="Export vehicle sightings"
              iconLeft={<Icon name="download" size={14} color={theme.colors.interactive.buttons.default_foreground} />}
              onPress={onExportPress}
            />
          </View>
        )}
      </View>

      {/* Expanded panel */}
      {expanded && (
        <View
          style={{
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.foreground,
            zIndex: 1,
            padding: 16,
            gap: 16,
          }}
        >
          {/* Text filters */}
          <View flexDirection="row" flexWrap="wrap" gap="md">
            <View flex={1} style={{ minWidth: 140 }}>
              <SearchInputField
                value={filters.make}
                onChangeText={(v) => onFiltersChange("make", v)}
                onFocus={noop}
                onKeyPress={noop}
                placeholder="Make"
                label="Make"
              />
            </View>
            <View flex={1} style={{ minWidth: 140 }}>
              <SearchInputField
                value={filters.model}
                onChangeText={(v) => onFiltersChange("model", v)}
                onFocus={noop}
                onKeyPress={noop}
                placeholder="Model"
                label="Model"
              />
            </View>
            <View flex={1} style={{ minWidth: 140 }}>
              <SearchInputField
                value={filters.color}
                onChangeText={(v) => onFiltersChange("color", v)}
                onFocus={noop}
                onKeyPress={noop}
                placeholder="Colour"
                label="Colour"
              />
            </View>
            <View flex={1} style={{ minWidth: 140 }}>
              <SearchInputField
                value={filters.description}
                onChangeText={(v) => onFiltersChange("description", v)}
                onFocus={noop}
                onKeyPress={noop}
                placeholder="Description"
                label="Description"
              />
            </View>
          </View>

          {/* Vehicle type + Load status */}
          <View flexDirection="row" flexWrap="wrap" gap="md" style={{ zIndex: 300 }}>
            <View style={{ minWidth: 160, flex: 1, zIndex: 200 }}>
              <MultiSelect
                options={[...VEHICLE_TYPE_VALUES]}
                value={filters.types}
                onChange={(value) => onFiltersChange("types", [...value])}
                getOptionLabel={(v) => toSentenceCase(VEHICLE_TYPE_LABELS[v], { lowercaseRest: true })}
                getOptionId={(v) => v}
                groupBy={(v) => VEHICLE_TYPE_GROUP[v] ?? ""}
                placeholder="Vehicle type"
                label="Vehicle type"
                filterSelectedOptions={false}
                maxTags={2}
              />
            </View>
            <View style={{ minWidth: 140, flex: 1, zIndex: 150 }}>
              <MultiSelect
                options={loadStatusOptions}
                value={filters.loadStatuses}
                onChange={(value) => onFiltersChange("loadStatuses", [...value])}
                getOptionLabel={(s) => toSentenceCase(s, { lowercaseRest: true })}
                getOptionId={(s) => s}
                placeholder="Load status"
                label="Load status"
                filterSelectedOptions={false}
                maxTags={2}
              />
            </View>
          </View>

          {/* Site + Cameras */}
          <View flexDirection="row" flexWrap="wrap" gap="md" style={{ zIndex: 200 }}>
            <View style={{ minWidth: 180, flex: 1, zIndex: 250 }}>
              <Autocomplete
                options={siteOptions}
                value={selectedSiteOption}
                onChange={(site) => onSiteChange(site?.siteId ?? null)}
                getOptionLabel={(s) => s.siteName}
                getOptionId={(s) => s.siteId}
                placeholder="All sites"
                label="Site"
              />
            </View>
            {allCameraOptions.length > 0 && (
              <View style={{ minWidth: 200, flex: 2, zIndex: 200 }}>
                <MultiSelect
                  options={allCameraOptions}
                  value={selectedCameraOptions}
                  onChange={(value) => handleCamerasChange(value)}
                  getOptionLabel={(o) => {
                    try {
                      const asset = AssetId.unique(AssetId.fromString(o.assetId));
                      const cam = o.cameraId.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                      return `${asset}/${cam}`;
                    } catch {
                      return o.cameraId;
                    }
                  }}
                  getOptionId={(o) => `${o.assetId}:${o.cameraId}`}
                  groupBy={(o) => {
                    try {
                      return AssetId.unique(AssetId.fromString(o.assetId));
                    } catch {
                      return o.assetId;
                    }
                  }}
                  placeholder="Filter cameras..."
                  label="Cameras"
                  filterSelectedOptions={false}
                  // Camera labels are long ("CAM_11307/Camera B") and share a prefix, so
                  // they must wrap rather than truncate to stay distinguishable. maxTags is
                  // an overflow guard here, not a routine collapse.
                  chipLayout="wrap"
                  maxTags={12}
                />
              </View>
            )}
          </View>

          {/* Attributes */}
          <View flexDirection="row" flexWrap="wrap" gap="md" style={{ zIndex: 100 }}>
            <View style={{ minWidth: 160, flex: 1, zIndex: 130 }}>
              <Autocomplete
                options={[...TRAY_TYPES]}
                value={trayValue}
                onChange={(value) => handleTrayChange(value)}
                getOptionLabel={(v) => getTrayTypeLabel(v)}
                getOptionId={(v) => v}
                placeholder="Tray type"
                label="Tray type"
              />
            </View>
            <View style={{ minWidth: 160, flex: 1, zIndex: 120 }}>
              {/* Case-insensitive substring match against the signage text VT read off the vehicle. */}
              <SearchInputField
                value={signTextValue}
                onChangeText={(v) => handleSignTextChange(v)}
                onFocus={noop}
                onKeyPress={noop}
                placeholder="Sign text"
                label="Sign text"
                validate={(v) => {
                  const trimmed = v.trim();
                  if (trimmed.length > 0 && trimmed.length < 2) return "Min. 2 characters";
                  return undefined;
                }}
              />
            </View>
            <View style={{ minWidth: 160, flex: 1 }}>
              <Pressable
                onPress={handleTrailerToggle}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isTowingTrailer }}
                accessibilityLabel={getAttributeLabel("trailer")}
              >
                <View flexDirection="row" alignItems="center" gap="sm" pl="md" h={44} pointerEvents="none">
                  <Checkbox checked={isTowingTrailer} />
                  <Text textStyle="sm/regular" color="text">
                    {getAttributeLabel("trailer")}
                  </Text>
                </View>
              </Pressable>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const exportStyles = StyleSheet.create({
  wide: { marginLeft: "auto" },
  narrow: { flex: 1, minWidth: "100%" },
});
